import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, Heart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DonateSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    if (sessionId) {
      checkStatus();
    }
  }, [sessionId]);

  const checkStatus = async () => {
    try {
      const response = await axios.get(`${API}/donate/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setStatus('success');
      } else {
        setStatus('pending');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center py-12" data-testid="donate-success-page">
      <div className="text-center max-w-md mx-auto px-4">
        {status === 'checking' && (
          <>
            <Loader2 className="h-16 w-16 mx-auto mb-6 animate-spin text-slate-400" />
            <h1 className="font-serif text-2xl font-medium mb-4">Processing...</h1>
            <p className="text-slate-500">Confirming your donation...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-20 w-20 mx-auto mb-6 text-green-500" />
            <h1 className="font-serif text-3xl font-medium mb-4">Thank You!</h1>
            <p className="text-slate-600 mb-8">
              Your generous donation helps us continue our mission of holding 
              politicians accountable. Together, we're making a difference.
            </p>
            <div className="flex items-center justify-center space-x-2 text-red-500 mb-8">
              <Heart className="h-5 w-5 fill-current" />
              <span className="font-medium">You're amazing!</span>
            </div>
            <div className="space-x-4">
              <Link to="/">
                <Button className="btn-primary">Back to News</Button>
              </Link>
              <Link to="/compare">
                <Button variant="outline">View Comparison</Button>
              </Link>
            </div>
          </>
        )}

        {status === 'pending' && (
          <>
            <Loader2 className="h-16 w-16 mx-auto mb-6 text-yellow-500" />
            <h1 className="font-serif text-2xl font-medium mb-4">Processing Payment</h1>
            <p className="text-slate-500 mb-8">
              Your payment is being processed. This may take a moment.
            </p>
            <Button onClick={checkStatus} variant="outline">
              Check Again
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="h-16 w-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-3xl">?</span>
            </div>
            <h1 className="font-serif text-2xl font-medium mb-4">Something Went Wrong</h1>
            <p className="text-slate-500 mb-8">
              We couldn't confirm your donation. If you were charged, please contact us.
            </p>
            <Link to="/donate">
              <Button className="btn-primary">Try Again</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
